import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaArrowLeft,
  FaMoneyBillWave,
  FaCreditCard,
  FaCheckDouble,
} from "react-icons/fa";
import AdminLayout from "../../components/AdminLayout";
import api from "../../utils/api";
import { usePopup } from "../../context/PopupContext";
import {
  toLocalizedNumber,
  toLocalizedPrice,
  toLocalizedStatus,
} from "../../utils/localeUtils";
import { getImageUrl } from "../../utils/imageUtils";
import "./AdminOrderDetail.css";

const AdminOrderDetail = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = usePopup();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (!userData) {
      navigate("/login");
      return;
    }
    try {
      if (JSON.parse(userData).role !== "admin") {
        navigate("/");
        return;
      }
    } catch {
      navigate("/login");
      return;
    }
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/orders/${id}`);
      setOrder(res.data);
    } catch {
      showToast(
        t("adminOrderDetail.loadFailed", "Could not load order"),
        "error",
      );
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const markCompleted = async () => {
    if (!order) return;
    setSaving(true);
    try {
      await api.put(`/orders/${order._id}/status`, {
        orderStatus: "completed",
      });
      showToast(
        t("adminOrderDetail.markedCompleted", "Order marked as completed"),
        "success",
      );
      await fetchOrder();
    } catch (err) {
      showToast(
        err.response?.data?.message ||
          t("adminOrderDetail.updateFailed", "Update failed"),
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const status = order?.orderStatus || order?.status || "pending";
  const isCod = (order?.paymentMethod || "").toLowerCase() === "cod";

  return (
    <AdminLayout>
      <div className="admin-order-detail">
        <div className="admin-order-detail-head">
          <Link to="/admin/orders" className="admin-order-detail-back">
            <FaArrowLeft />{" "}
            {t("adminOrderDetail.backToOrders", "Back to orders")}
          </Link>
          <h1 className="admin-order-detail-title">
            {t("adminOrderDetail.title", "Order")} #{id?.slice(-8)}
          </h1>
        </div>

        {loading ? (
          <div className="admin-order-detail-loading">
            <div className="admin-order-detail-spinner" />
            <p>{t("adminOrderManagement.loading")}</p>
          </div>
        ) : !order ? (
          <p className="admin-order-detail-error">
            {t("adminOrderDetail.notFound", "Order not found")}
          </p>
        ) : (
          <>
            <div className="admin-order-detail-grid">
              <section className="admin-order-detail-card">
                <h2>{t("adminOrderDetail.statusSection", "Status")}</h2>
                <p className="admin-order-detail-status">
                  {toLocalizedStatus(status, t)}
                </p>
                {status === "received" && (
                  <button
                    type="button"
                    className="admin-order-detail-btn-complete"
                    onClick={markCompleted}
                    disabled={saving}
                  >
                    <FaCheckDouble />{" "}
                    {t("adminOrderDetail.markCompleted", "Mark as completed")}
                  </button>
                )}
              </section>

              <section className="admin-order-detail-card">
                <h2>{t("adminOrderDetail.payment", "Payment")}</h2>
                <p>
                  <span className="admin-order-detail-pay-badge">
                    {isCod ? (
                      <>
                        <FaMoneyBillWave /> COD
                      </>
                    ) : (
                      <>
                        <FaCreditCard /> Online
                      </>
                    )}
                  </span>
                </p>
                <p className="admin-order-detail-muted">
                  {t("adminOrderDetail.paymentStatus", "Payment status")}:{" "}
                  {toLocalizedStatus(order.paymentStatus || "pending", t)}
                </p>
                <p className="admin-order-detail-total">
                  Rs. {toLocalizedPrice(order.totalPrice || 0)}
                </p>
              </section>

              <section className="admin-order-detail-card">
                <h2>{t("adminOrderDetail.consumer", "Consumer")}</h2>
                <p>
                  <strong>{order.consumer?.name}</strong>
                </p>
                <p className="admin-order-detail-muted">
                  {order.consumer?.email}
                </p>
                <p className="admin-order-detail-muted">
                  {order.consumer?.phone}
                </p>
              </section>

              <section className="admin-order-detail-card">
                <h2>{t("adminOrderDetail.farmer", "Farmer")}</h2>
                <p>
                  <strong>{order.farmer?.name}</strong>
                </p>
                <p className="admin-order-detail-muted">
                  {order.farmer?.email}
                </p>
                <Link
                  to={`/users/${order.farmer?.username || order.farmer?._id}`}
                  className="admin-order-detail-link"
                >
                  {t("adminOrderDetail.viewFarmer", "View farmer profile")}
                </Link>
              </section>

              {order.shippingAddress && (
                <section className="admin-order-detail-card admin-order-detail-card--wide">
                  <h2>{t("adminOrderDetail.shipping", "Shipping")}</h2>
                  <p>{order.shippingAddress.fullName}</p>
                  <p className="admin-order-detail-muted">
                    {[
                      order.shippingAddress.street,
                      order.shippingAddress.city,
                      order.shippingAddress.district,
                      order.shippingAddress.province,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                  <p className="admin-order-detail-muted">
                    {order.shippingAddress.phone}
                  </p>
                  {order.shippingAddress.deliveryMethod && (
                    <p className="admin-order-detail-muted">
                      {t("adminOrderDetail.delivery", "Delivery")}:{" "}
                      {order.shippingAddress.deliveryMethod}
                    </p>
                  )}
                </section>
              )}
            </div>

            <section className="admin-order-detail-card admin-order-detail-items">
              <h2>{t("adminOrderManagement.items")}</h2>
              <table className="admin-order-detail-table">
                <thead>
                  <tr>
                    <th>{t("adminProductManagement.productName")}</th>
                    <th>{t("adminOrderDetail.qty", "Qty")}</th>
                    <th>{t("adminOrderDetail.unitPrice", "Unit")}</th>
                    <th>{t("adminOrderDetail.lineTotal", "Line total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(order.items || []).map((item, idx) => {
                    const name = item.name || item.product?.name || "—";
                    const img = item.product?.images?.[0];
                    return (
                      <tr key={idx}>
                        <td>
                          <div className="admin-order-detail-item-cell">
                            {img && (
                              <img
                                src={getImageUrl(img)}
                                alt=""
                                className="admin-order-detail-thumb"
                              />
                            )}
                            <span>{name}</span>
                          </div>
                        </td>
                        <td>{toLocalizedNumber(item.quantity)}</td>
                        <td>Rs. {toLocalizedPrice(item.price)}</td>
                        <td>
                          Rs.{" "}
                          {toLocalizedPrice(
                            (item.price || 0) * (item.quantity || 0),
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminOrderDetail;
